import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { api, ApiError } from "@/lib/api";

interface Org {
  id: string;
  slug: string;
  name: string;
  createdAt: string;
}

export function OrgOverview() {
  const { org } = useParams<{ org: string }>();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["org", org],
    queryFn: () => api.get<{ org: Org }>(`/orgs/${org}`).then((r) => r.org),
  });

  const [name, setName] = useState("");
  useEffect(() => {
    if (q.data) setName(q.data.name);
  }, [q.data]);

  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: () => api.patch<{ org: Org }>(`/orgs/${org}`, { name }),
    onSuccess: (r) => {
      qc.setQueryData(["org", org], r.org);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  if (q.isLoading) {
    return (
      <div className="py-10 text-center text-sm text-muted-foreground inline-flex items-center justify-center w-full gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle>Organization</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="org-name">Name</Label>
              <Input id="org-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="org-slug">Slug</Label>
              <Input id="org-slug" value={q.data?.slug ?? ""} readOnly className="bg-subtle/60 cursor-not-allowed font-mono" />
            </div>
          </div>

          {save.error instanceof ApiError && (
            <div className="text-xs text-danger bg-danger-subtle border border-danger/30 rounded-md px-2.5 py-1.5">
              {save.error.message}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            {saved && (
              <span className="text-xs text-success inline-flex items-center gap-1">
                <Check className="h-3.5 w-3.5" /> Saved
              </span>
            )}
            <Button
              variant="primary"
              disabled={!name || name === q.data?.name || save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              Save
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
